import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import {
  sanityDraftsClient,
  changelogEntryFilter,
  previewsSanityDrafts,
} from '@dashboard/lib/sanity/client';
import { recentChangelogEntriesKey } from '@dashboard/lib/queryKeys';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';

const recentChangelogEntriesQuery = `*[${changelogEntryFilter}] | order(date desc)[0...20]{
  _id,
  title,
  date,
  launchType,
  body[] {
    ...,
    _type == "image" => {
      ...,
      asset->{
        ...
      }
    }
  }
}`;

// A new account has seen none of the older updates, but they are not news to it either.
export function getUnseenUpdates(updates = [], user) {
  const seenIds = new Set(user?.seenChangelogIds || []);
  const cutoff = user?.closedChangelogAt || user?.createdAt;

  return updates.filter(
    update =>
      update.launchType !== 'improvement' &&
      !seenIds.has(update._id) &&
      (!cutoff || new Date(cutoff) < new Date(update.date)),
  );
}

const isLaunch = update => update.launchType === 'launch';

export function pickLaunch(unseenUpdates) {
  return unseenUpdates.find(isLaunch) || null;
}

// Buttons link to checkoutpage.com/dashboard, so on staging and local stacks they stay in this dashboard.
export function getLaunchAction(update) {
  const fallback = { label: 'Read the full update', to: `/updates#${update._id}` };
  const button = update.body?.find(block => block._type === 'button' && block.label);
  if (!button?.link?.href) return fallback;

  let url;
  try {
    url = new URL(button.link.href, 'https://checkoutpage.com');
  } catch {
    return fallback;
  }
  const isOurs = url.origin === window.location.origin || url.hostname === 'checkoutpage.com';
  if (isOurs && /^\/dashboard(\/|$)/.test(url.pathname)) {
    const path = url.pathname.replace(/^\/dashboard/, '') || '/';
    return { label: button.label, to: `${path}${url.search}${url.hash}` };
  }
  return { label: button.label, href: button.link.href };
}

export function getCardUpdates(unseenUpdates) {
  return unseenUpdates.filter(update => !isLaunch(update));
}

export function useUpdateNotification() {
  const { user, setUser } = useAuth();
  const { updateUser } = useUserQueries();
  const [previewSeenIds, setPreviewSeenIds] = useState([]);

  const { data: updates } = useQuery({
    queryKey: recentChangelogEntriesKey,
    queryFn: () => sanityDraftsClient.fetch(recentChangelogEntriesQuery),
    staleTime: 5 * 60 * 1000,
  });

  const isReady = Boolean(updates) && (previewsSanityDrafts || Boolean(user?._id));
  const unseenUpdates = isReady
    ? getUnseenUpdates(updates, previewsSanityDrafts ? { seenChangelogIds: previewSeenIds } : user)
    : [];

  // Not overwritten with the response: a slower save of an older list would bring back an update just dismissed.
  const saveUser = values => {
    setUser({ ...user, ...values });
    return updateUser(values, user._id);
  };

  const markUpdateAsSeen = async id => {
    if (previewsSanityDrafts) {
      setPreviewSeenIds(ids => [...ids, id]);
      return;
    }
    const seenIds = user.seenChangelogIds || [];
    if (seenIds.includes(id)) return;
    await saveUser({ seenChangelogIds: [...seenIds, id] });
  };

  const markUpdatesAsSeen = async () => {
    if (previewsSanityDrafts) {
      setPreviewSeenIds((updates || []).map(update => update._id));
      return;
    }
    if (!user?._id) return;
    await saveUser({ closedChangelogAt: new Date().toISOString(), seenChangelogIds: [] });
  };

  return { isReady, unseenUpdates, markUpdateAsSeen, markUpdatesAsSeen };
}
